import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import DashboardTopbar from "components/DashboardTopbar";
import DashboardMenuBar from "components/DashboardMenuBar";
import { useAppContext } from "components/Context/AppContext";
import PortfolioTab from "./PortfolioTab";
import AlternativesTab from "./AlternativesTab";
import "./whatif.css";
import "styles/dashboard.css";

const API = process.env.REACT_APP_API_URL || "http://localhost:5050/api";
const token = () => sessionStorage.getItem("token");

export default function WhatIfAnalysis() {
  const { selectedAccount } = useAppContext();
  const [searchParams]            = useSearchParams();
  const navigate                  = useNavigate();
  const preselectedHandled        = useRef(false);
  const [theme, setTheme]         = useState(() => localStorage.getItem("trg_theme") || "dark");
  const [activeTab, setActiveTab]   = useState("portfolio");
  const [available, setAvailable]     = useState([]);
  const [portfolios, setPortfolios]   = useState([]);
  const [allocData, setAllocData]     = useState({});
  const [altsData, setAltsData]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [portMetrics, setPortMetrics] = useState({});
  const [altPanel, setAltPanel]       = useState(null);
  const [riskLimits, setRiskLimits]   = useState(null); // /settings/limits → risk (vol/VaR limit lines)
  const [bmkLimits,  setBmkLimits]    = useState(null); // /settings/limits → bmk (benchmark Sharpe lines)

  const fetchAllocations = useCallback(async (portId) => {
    const t = token();
    try {
      const res = await fetch(`${API}/whatif/portfolio/${portId}/allocations?token=${t}`);
      if (!res.ok) return;
      const data = await res.json();
      if (data && !data.error) {
        setAllocData(prev => ({ ...prev, [portId]: data }));
      }
    } catch {}
  }, []);

  const extractMetrics = (port) => ({
    vol:       port.vol,
    vrisk:     port.vrisk,
    vriskPct:  port.vriskPct,
    sharpeVol: port.sharpeVol,
    sharpeVar: port.sharpeVar,
    conc:      port.conc,
  });

  // Fetch available portfolios once
  useEffect(() => {
    const t = token();
    const aid = selectedAccount?.account_id;
    const url = aid
      ? `${API}/whatif/portfolios?token=${t}&account_id=${aid}`
      : `${API}/whatif/portfolios?token=${t}`;
    fetch(url)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data) && data.length) {
          setAvailable(data);
          const first = { ...data[0], weights: { ...data[0].weights } };
          setPortfolios([first]);
          setPortMetrics({ [first.id]: extractMetrics(first) });
          fetchAllocations(first.id);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [selectedAccount, fetchAllocations]);

  // Fetch risk limits (Settings) for the metric-chart limit lines
  useEffect(() => {
    const t = token();
    const aid = selectedAccount?.account_id;
    if (!t || !aid) return;
    fetch(`${API}/settings/limits?account_id=${aid}&token=${t}`)
      .then(r => r.json())
      .then(d => { setRiskLimits(d?.risk ?? null); setBmkLimits(d?.bmk ?? null); })
      .catch(() => {});
  }, [selectedAccount]);

  // Pre-select a portfolio passed via ?add_port_id=<id> from the Portfolios page
  useEffect(() => {
    if (available.length === 0 || preselectedHandled.current) return;
    const addId = searchParams.get("add_port_id");
    if (!addId) return;
    preselectedHandled.current = true;
    const port = available.find(p => String(p.id) === String(addId));
    navigate("/whatif", { replace: true });
    if (!port || port.id === available[0].id) return;
    const anchor  = { ...available[0], weights: { ...available[0].weights } };
    const newPort = { ...port,         weights: { ...port.weights } };
    setPortfolios([anchor, newPort]);
    setPortMetrics({
      [anchor.id]:  extractMetrics(anchor),
      [newPort.id]: extractMetrics(newPort),
    });
    fetchAllocations(anchor.id);
    fetchAllocations(newPort.id);
  }, [available]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch alternatives panel data — deferred until the Alternatives tab is opened
  useEffect(() => {
    if (activeTab !== 'alternatives') return;
    const t = token();
    const aid = selectedAccount?.account_id;
    if (!aid) return;
    fetch(`${API}/whatif/alternatives/panel?token=${t}&account_id=${aid}`)
      .then(r => r.json())
      .then(data => { if (data && !data.error) setAltPanel(data); })
      .catch(() => {});
  }, [selectedAccount, activeTab]);

  // Fetch alts positions — deferred until the Alternatives tab is opened
  useEffect(() => {
    if (activeTab !== 'alternatives') return;
    const t = token();
    const aid = selectedAccount?.account_id;
    if (!aid) return;
    fetch(`${API}/whatif/alternatives/positions?token=${t}&account_id=${aid}`)
      .then(r => r.json())
      .then(alts => setAltsData(Array.isArray(alts) ? alts : []))
      .catch(() => {});
  }, [selectedAccount, activeTab]);

  function handleAdd(port) {
    if (portfolios.length >= 5 || portfolios.find(p => p.id === port.id)) return;
    const newPort = { ...port, weights: { ...port.weights } };
    setPortfolios(prev => [...prev, newPort]);
    setPortMetrics(prev => ({ ...prev, [port.id]: extractMetrics(port) }));
    fetchAllocations(port.id);
  }

  function handleRemove(id) {
    setPortfolios(prev => prev.filter(p => p.id !== id));
    setPortMetrics(prev => { const n = { ...prev }; delete n[id]; return n; });
    setAllocData(prev => { const n = { ...prev }; delete n[id]; return n; });
  }

  return (
    <>
      <Helmet>
        <title>What-If Analysis – Tail Risk Global</title>
        <meta name="description" content="Compare portfolio allocations and analyse risk/return impact." />
      </Helmet>

      <div className="trg-shell">
        <DashboardTopbar />
        <DashboardMenuBar activePage="What-If" onThemeChange={setTheme} />

        <nav className="wif-subnav">
          <button
            className={`wif-subnav-item${activeTab === "portfolio" ? " active" : ""}`}
            onClick={() => setActiveTab("portfolio")}
          >
            Portfolio
          </button>
          <button
            className={`wif-subnav-item${activeTab === "alternatives" ? " active" : ""}`}
            onClick={() => setActiveTab("alternatives")}
          >
            Alternatives
          </button>
        </nav>

        <main className={`wif-body${theme === "light" ? " theme-light" : ""}`}>
          {activeTab === "alternatives" ? (
            <AlternativesTab alts={altsData} panel={altPanel} accountId={selectedAccount?.account_id} />
          ) : loading ? (
            <div className="wif-loading">Loading portfolios…</div>
          ) : (
            <PortfolioTab
              portfolios={portfolios}
              available={available}
              allocData={allocData}
              portMetrics={portMetrics}
              onAdd={handleAdd}
              onRemove={handleRemove}
              theme={theme}
              riskLimits={riskLimits}
              bmkLimits={bmkLimits}
            />
          )}
        </main>
      </div>
    </>
  );
}
